import { AfterViewInit, Component, DestroyRef, ElementRef, EventEmitter, inject, Input, Output, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { UiKitModule } from "@app/ui-kit.module";
import { ConfigurationWizardHeaderComponent } from "@components/configuration-wizard-header/configuration-wizard-header.component";
import { Gas, LocalizeGasNamePipe } from "@domain/gas/gas";
import { GasService } from "@domain/gas/gas.service";
import { FgdsProductType, Product } from "@domain/product/product";
import { ProductService } from "@domain/product/product.service";
import { FgdsProject as Project } from "@domain/project/fgds-project";
import { debounceTime, first, fromEvent, map, Observable, of, switchMap } from "rxjs";

@Component({
  selector: "app-gas-selector",
  templateUrl: "./gas-selector.component.html",
  styleUrls: ["./gas-selector.component.scss"],
  imports: [UiKitModule, LocalizeGasNamePipe, ConfigurationWizardHeaderComponent],
  providers: [LocalizeGasNamePipe],
  standalone: true,
})
export class GasSelectorComponent implements AfterViewInit {
  private gasService = inject(GasService);
  private localizeGasNamePipe = inject(LocalizeGasNamePipe);
  private productService = inject(ProductService);
  private destroyRef = inject(DestroyRef);

  @Input() project!: Project;

  /**
   * Restricts the selectable gases to those referencing at least one product of the given
   * FGDS product type. This is the authoritative eligibility check for a configuration wizard.
   */
  @Input() set productType(productType: FgdsProductType | undefined) {
    this._productType = productType;
    this.applyGasFilter();
  }

  @Input() set originalGas(gas: Gas | undefined) {
    if (gas) {
      this.select(gas);
    }
  }

  @Input() set originalTransmitter(transmitter: Product | undefined) {
    this.transmitter = transmitter;
    if (transmitter) {
      this.listMatchingGasesOnly(transmitter).subscribe();
    }
  }

  @Output() gasSelect$ = new EventEmitter<Gas>();

  protected filteredGases$!: Observable<Gas[]>;
  protected selectedGas?: Gas;
  protected transmitter?: Product;
  private _productType?: FgdsProductType;
  private searchQuery = "";

  @ViewChild("searchField")
  private readonly searchField!: ElementRef;
  private gases$!: Observable<Gas[]>;

  constructor() {
    this.gases$ = this.gasService.loadAll().pipe(
      first(),
      map((results) => {
        if (!results) return [];
        return results.sort((a, b) => {
          if (this.localizeGasNamePipe.transform(a) < this.localizeGasNamePipe.transform(b)) {
            return -1;
          }
          if (this.localizeGasNamePipe.transform(a) > this.localizeGasNamePipe.transform(b)) {
            return 1;
          }
          return 0;
        });
      }),
    );
    this.applyGasFilter();
  }

  ngAfterViewInit(): void {
    fromEvent(this.searchField.nativeElement, "input")
      .pipe(debounceTime(500), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.searchQuery = this.searchField.nativeElement.value;
        this.applyGasFilter();
      });
  }

  protected select(selectedGas: Gas) {
    this.selectedGas = selectedGas;
    this.gasSelect$.emit(this.selectedGas);
  }

  private searchFilter(item: Gas, query: string): boolean {
    const gasName = this.localizeGasNamePipe.transform(item);
    const lowerQueryString = query.toLocaleLowerCase();
    return (
      !!gasName &&
      (gasName.toLocaleLowerCase().indexOf(lowerQueryString) >= 0 ||
        item.id.toLocaleLowerCase().indexOf(lowerQueryString) >= 0 ||
        item.definition.formula.toLocaleLowerCase().indexOf(lowerQueryString) >= 0)
    );
  }

  private listMatchingGasesOnly(transmitter: Product) {
    return this.gases$.pipe(
      map((gases) => {
        const matchingGases = gases.filter((gas) =>
          gas.products.map((gasProduct) => gasProduct.productId).includes(transmitter.id),
        );
        this.gases$ = of(matchingGases);
        this.applyGasFilter();
      }),
    );
  }

  private applyGasFilter() {
    if (!this.gases$) return;

    let filteredGases$ = this.gases$;
    if (this._productType !== undefined) {
      filteredGases$ = filteredGases$.pipe(switchMap((gases) => this.filterByProductType(gases)));
    }
    if (this.searchQuery) {
      filteredGases$ = filteredGases$.pipe(map((gases) => gases.filter((gas) => this.searchFilter(gas, this.searchQuery))));
    }
    this.filteredGases$ = filteredGases$;
  }

  private filterByProductType(gases: Gas[]): Observable<Gas[]> {
    const productIds = [...new Set(gases.flatMap((gas) => gas.products.map((gasProduct) => gasProduct.productId)))];
    if (!productIds.length) {
      return of([]);
    }

    return this.productService.getProductsByIds(productIds, this._productType!).pipe(
      map((products) => {
        const matchingProductIds = new Set(products.map((product) => product.id));
        return gases.filter((gas) => gas.products.some((gasProduct) => matchingProductIds.has(gasProduct.productId)));
      }),
    );
  }
}
